import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Trash2, ShieldCheck } from "lucide-react";

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the workspace?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="p-10 max-w-4xl mx-auto">
      <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Admin</p>
      <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Team</h1>
      <p className="text-zinc-500 mt-2 text-sm">Manage workspace members and their roles. Admins can manage the team, brand styles and any content; designers manage only their own.</p>

      <div className="mt-8 border border-zinc-200 bg-white rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
          <span className="col-span-5">Member</span>
          <span className="col-span-4">Role</span>
          <span className="col-span-3 text-right">Actions</span>
        </div>
        {users.map((u) => (
          <div key={u.id} data-testid={`team-member-${u.id}`} className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50">
            <div className="col-span-5 min-w-0">
              <p className="text-sm font-medium truncate flex items-center gap-1.5">
                {u.name}
                {u.role === "admin" && <ShieldCheck size={13} className="text-zinc-400" />}
                {u.id === user?.id && <span className="text-[10px] text-zinc-400">(you)</span>}
              </p>
              <p className="text-xs text-zinc-400 truncate">{u.email}</p>
            </div>
            <div className="col-span-4">
              <select data-testid={`team-role-select-${u.id}`} value={u.role} disabled={u.id === user?.id}
                onChange={(e) => changeRole(u.id, e.target.value)}
                className="border border-zinc-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed">
                <option value="designer">Designer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="col-span-3 text-right">
              {u.id !== user?.id && (
                <button data-testid={`team-delete-${u.id}`} onClick={() => remove(u.id, u.name)}
                  className="p-2 rounded-md text-zinc-400 hover:bg-red-50 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
